import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { deactivateDeal } from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";
import { useDealsListStore } from "@/store/deals-list-store";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface DeactivateDealDialogProps {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  dealName: string;
  workspaceId: string;
}

export function DeactivateDealDialog({
  isOpen,
  onClose,
  dealId,
  dealName,
  workspaceId,
}: DeactivateDealDialogProps) {
  const [isDeactivating, setIsDeactivating] = useState(false);
  const updateDeal = useDealsListStore((s) => s.updateDeal);
  const queryClient = useQueryClient();

  const handleDeactivate = async () => {
    if (isDeactivating) return;
    setIsDeactivating(true);
    try {
      await deactivateDeal(workspaceId, dealId);
      updateDeal(dealId, { aiTracked: false });
      queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
      toast.success("Deal marked as not premium");
      onClose();
    } catch (error) {
      console.error("Failed to deactivate deal:", error);
      toast.error(getErrorMessage(error, "Failed to update deal"));
    } finally {
      setIsDeactivating(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && isDeactivating) return;
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark this deal as not premium?</DialogTitle>
          <DialogDescription>
            {dealName} will no longer be a premium deal and premium reports will
            stop generating. You can make it premium again later by generating a
            premium report.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeactivating}>
            Cancel
          </Button>
          <Button onClick={handleDeactivate} disabled={isDeactivating}>
            {isDeactivating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Mark as not premium"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
